import {
  mergeWorkoutLayersWithExternal,
  tagPrescriptionLayer,
  Workout,
} from '@strength/domain-workout';
import { DateTime } from 'luxon';
import { v5 as uuidv5 } from 'uuid';
import { ModifiedPublishedWorkoutLayer } from '../../actuals/types';
import {
  AthleteActualWorkoutLayer,
  AthleteDraftWorkoutLayer,
  AthletePublishedWorkoutLayer,
  BlockDraftWorkoutLayer,
  BlockPublishedWorkoutLayer,
} from '../types';
import { AthletePublishedWorkoutLayer as AthleteLayer } from '../../actuals/types';
import { ActualWorkout } from './types';

// Source ranking for actuals merges. 'Prescription' is a sentinel (see
// layer.ts / merge-workout-layers.ts) that is temporarily assigned to
// prescription-layer setMetrics before merging so they lose to every
// actual-layer source, including user-entered nulls. The merge function
// strips the sentinel from the returned Workout automatically.
const ACTUALS_SOURCE_RANKING = [
  'Manual Override',
  'Catapult',
  'Player Entered',
  null,
  'Match Stats',
  'Prescription',
] as const;

/**
 * Namespace for the deterministic UUIDv5 ids of synthesized rows for days with
 * no scheduled workout. Must never change: the server permanently keys a
 * manufactured block workout to the id the client sends, so a different
 * namespace (or a random id) would manufacture a duplicate day on the next
 * save.
 */
const EMPTY_DAY_WORKOUT_NAMESPACE = 'fbe1e975-578c-4d99-86b0-474573f6f64e';

export type EmptyDaySynthesisOptions = {
  blockId: string;
  /** ISO dates (YYYY-MM-DD) bounding the block. Days outside are never synthesized. */
  blockRange: { startDate: string; endDate: string };
  blockDraftWorkoutLayers: BlockDraftWorkoutLayer[];
  athleteDraftWorkoutLayers: AthleteDraftWorkoutLayer[];
  /**
   * Dates already rendered by another source (e.g. the cross-block overlay),
   * so synthesis does not add a second row for the same day.
   */
  excludedDates: ReadonlySet<string>;
};

export const getActualWorkoutsForLayers = (
  actualAthleteLayers: AthleteActualWorkoutLayer[],
  athletePublishedWorkoutLayers: AthletePublishedWorkoutLayer[],
  blockPublishedWorkoutLayers: BlockPublishedWorkoutLayer[],
  modifiedPublishedWorkoutLayers: ModifiedPublishedWorkoutLayer[],
  profileId: string | null,
  emptyDayOptions?: EmptyDaySynthesisOptions,
): ActualWorkout[] => {
  const actualByWorkoutId = new Map(
    actualAthleteLayers.map((a) => [a.workoutId, a]),
  );
  const modifierByWorkoutId = new Map(
    modifiedPublishedWorkoutLayers.map((m) => [m.workoutId, m]),
  );
  const currentBlockByWorkoutId = new Map(
    blockPublishedWorkoutLayers
      .filter((b) => b.isCurrent)
      .map((b) => [b.workoutId, b]),
  );

  const mergedAthleteLayers = athletePublishedWorkoutLayers.map((layer) => {
    return {
      ...layer,
      actual: actualByWorkoutId.get(layer.workoutId),
      modifier: modifierByWorkoutId.get(layer.workoutId),
      blockLayer: currentBlockByWorkoutId.get(layer.workoutId),
    };
  });

  const athleteWorkouts = getAthleteWorkoutsForLayers(mergedAthleteLayers);

  // Days with no published athlete layer produce no row above, so their slot
  // stays disabled. Synthesize an editable row from the current block layer
  // instead — a coach can enter a manual actual even when nothing was ever
  // published for the athlete.
  if (!profileId) return athleteWorkouts;

  const publishedWorkoutIds = new Set(
    athletePublishedWorkoutLayers.map((layer) => layer.workoutId),
  );
  const synthesizedWorkouts = [...currentBlockByWorkoutId.values()]
    .filter((blockLayer) => !publishedWorkoutIds.has(blockLayer.workoutId))
    .map((blockLayer) =>
      getSynthesizedActualWorkout(
        blockLayer,
        profileId,
        actualByWorkoutId.get(blockLayer.workoutId),
      ),
    );

  // Dates with no scheduled workout at all produce no row above either.
  // Synthesize one from the block's closest-date published layer so a coach
  // can enter actuals for the day; the server manufactures the scaffolding on
  // commit (see getEmptyDayActualWorkouts).
  const emptyDayWorkouts = getEmptyDayActualWorkouts(
    actualAthleteLayers,
    blockPublishedWorkoutLayers,
    athletePublishedWorkoutLayers,
    profileId,
    emptyDayOptions,
  );

  return [...athleteWorkouts, ...synthesizedWorkouts, ...emptyDayWorkouts];
};

/**
 * Rows for days inside the block range that have no scheduled workout — no
 * current block layer, no athlete layer, no draft, and no cross-block overlay.
 *
 * There is no block layer to clone client-side, so the shape (sections,
 * exercises, real metric ids, set ids) is cloned verbatim from the block's
 * closest-date current published layer. The server donates the same layer when
 * it manufactures the day, which is what lets a later reload merge the saved
 * actual back onto the manufactured sets. Only the wrapper's workoutId and
 * assignedDate differ from the donor.
 */
const getEmptyDayActualWorkouts = (
  actualAthleteLayers: AthleteActualWorkoutLayer[],
  blockPublishedWorkoutLayers: BlockPublishedWorkoutLayer[],
  athletePublishedWorkoutLayers: AthletePublishedWorkoutLayer[],
  profileId: string,
  options: EmptyDaySynthesisOptions | undefined,
): ActualWorkout[] => {
  if (!options) return [];
  const { blockId, blockRange, excludedDates } = options;

  const start = DateTime.fromISO(blockRange.startDate).startOf('day');
  const end = DateTime.fromISO(blockRange.endDate).startOf('day');
  if (!start.isValid || !end.isValid || start > end) return [];

  const currentLayers = blockPublishedWorkoutLayers.filter(
    (layer) => layer.isCurrent && layer.blockId === blockId,
  );
  // No published workout means no shape to clone; the server refuses to
  // manufacture the day, so don't offer an editable cell either.
  if (currentLayers.length === 0) return [];

  const excluded = new Set<string>(excludedDates);
  const excludeDate = (assignedDate: string | null | undefined) => {
    if (assignedDate) excluded.add(assignedDate);
  };
  for (const layer of currentLayers) excludeDate(layer.assignedDate);
  for (const layer of athletePublishedWorkoutLayers) {
    excludeDate(layer.assignedDate);
  }
  for (const layer of options.blockDraftWorkoutLayers) {
    excludeDate(layer.assignedDate);
  }
  for (const layer of options.athleteDraftWorkoutLayers) {
    excludeDate(layer.assignedDate);
  }

  // A saved actual for a manufactured day can exist before its placeholder
  // layer syncs back, so match on the deterministic workoutId to merge the
  // persisted value into the row instead of re-synthesizing an empty one.
  const actualByWorkoutId = new Map(
    actualAthleteLayers.map((layer) => [layer.workoutId, layer]),
  );

  const workouts: ActualWorkout[] = [];
  for (let cursor = start; cursor <= end; cursor = cursor.plus({ days: 1 })) {
    const assignedDate = cursor.toISODate();
    if (!assignedDate || excluded.has(assignedDate)) continue;

    const workoutId = uuidv5(
      `${blockId}|${assignedDate}`,
      EMPTY_DAY_WORKOUT_NAMESPACE,
    );

    const donor = pickClosestAssignedDateLayer(currentLayers, assignedDate);
    if (!donor) continue;

    synthesizeEmptyDay(
      workouts,
      donor,
      profileId,
      blockId,
      assignedDate,
      workoutId,
      actualByWorkoutId.get(workoutId),
    );
  }
  return workouts;
};

const synthesizeEmptyDay = (
  workouts: ActualWorkout[],
  donor: BlockPublishedWorkoutLayer,
  profileId: string,
  blockId: string,
  assignedDate: string,
  workoutId: string,
  actual?: AthleteActualWorkoutLayer,
): void => {
  const syntheticLayer = donor.layer;

  workouts.push({
    assignedDate,
    context: {
      profileId,
      blockId,
      athleteVersion: 0,
      blockVersion: 0,
      blockLayerId: undefined,
      athleteLayerId: '',
      actualLayerId: actual?.id ?? '',
      workoutStatus: 'planned',
      assignedDate,
      syntheticLayer,
    },
    hasActual: !!actual,
    workout: mergeWorkoutLayersWithExternal(
      ACTUALS_SOURCE_RANKING,
      tagPrescriptionLayer({
        id: '',
        workoutId,
        name: donor.name,
        assignedDate,
        layer: syntheticLayer,
      }),
      actual,
    ),
  });
};

/**
 * Closest assignedDate to `targetDate`, ties toward the earlier date and
 * dateless layers ignored. Mirrors the server's manufactured-day shape donor
 * (pickClosestAssignedDateLayer) so both sides clone the same published layer
 * and therefore resolve the same set/metric ids.
 */
const pickClosestAssignedDateLayer = <
  T extends { assignedDate?: string | null },
>(
  layers: T[],
  targetDate: string,
): T | undefined => {
  const target = Date.parse(`${targetDate}T00:00:00Z`);
  let best: T | undefined;
  let bestDate: string | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const layer of layers) {
    const assignedDate = layer.assignedDate;
    if (!assignedDate) continue;
    const distance = Math.abs(Date.parse(`${assignedDate}T00:00:00Z`) - target);
    const isCloser = distance < bestDistance;
    const isEarlierTie =
      distance === bestDistance &&
      bestDate !== undefined &&
      assignedDate < bestDate;
    if (best === undefined || isCloser || isEarlierTie) {
      best = layer;
      bestDate = assignedDate;
      bestDistance = distance;
    }
  }
  return best;
};

// Mirrors getAthleteWorkoutsForLayers for a day that has a current block layer
// but no athlete published layer. athleteVersion 0 is the sentinel the server
// resolves to the version it assigns when it creates the placeholder layers.
// The block layer supplies the Match/Training/Modified set structure so the
// slot mounts its editable cells; there is no actual to merge.
const getSynthesizedActualWorkout = (
  blockLayer: BlockPublishedWorkoutLayer,
  profileId: string,
  actual?: AthleteActualWorkoutLayer,
): ActualWorkout => ({
  context: {
    profileId,
    blockId: blockLayer.blockId,
    athleteVersion: 0,
    blockVersion: blockLayer.version,
    blockLayerId: blockLayer.id,
    athleteLayerId: '',
    actualLayerId: actual?.id ?? '',
    workoutStatus: 'planned',
  },
  assignedDate: blockLayer.assignedDate ?? '',
  hasActual: !!actual,
  workout: mergeWorkoutLayersWithExternal(
    ACTUALS_SOURCE_RANKING,
    tagPrescriptionLayer(blockLayer),
    actual,
  ),
});

/**
 * Builds the merged workouts feeding the bowling load chart from the draft
 * pipeline so the chart reflects unpublished edits as soon as they are saved
 * locally. Merge order (later overrides earlier):
 *   block draft → athlete draft → athlete modified-published → athlete actual
 *
 * Inputs other than `blockDraftLayers` are expected to already be filtered to
 * the currently selected athlete.
 */
export const getChartWorkoutsForLayers = (
  blockDraftLayers: BlockDraftWorkoutLayer[],
  athleteDraftLayers: AthleteDraftWorkoutLayer[],
  modifiedAthleteLayers: ModifiedPublishedWorkoutLayer[],
  actualAthleteLayers: AthleteActualWorkoutLayer[],
): Workout[] => {
  const athleteDraftByWorkoutId = new Map(
    athleteDraftLayers.map((a) => [a.workoutId, a]),
  );
  const modifierByWorkoutId = new Map(
    modifiedAthleteLayers.map((m) => [m.workoutId, m]),
  );
  const actualByWorkoutId = new Map(
    actualAthleteLayers.map((a) => [a.workoutId, a]),
  );

  return blockDraftLayers.map((blockLayer) =>
    mergeWorkoutLayersWithExternal(
      ACTUALS_SOURCE_RANKING,
      tagPrescriptionLayer(blockLayer),
      tagPrescriptionLayer(athleteDraftByWorkoutId.get(blockLayer.workoutId)),
      tagPrescriptionLayer(modifierByWorkoutId.get(blockLayer.workoutId)),
      actualByWorkoutId.get(blockLayer.workoutId),
    ),
  );
};

const getAthleteWorkoutsForLayers = (
  athleteWorkouts: AthleteLayer[],
): ActualWorkout[] => {
  return athleteWorkouts.map((athleteLayer) => {
    const {
      profileId,
      blockId,
      actual,
      version,
      blockVersion,
      blockLayer,
      assignedDate,
      modifier,
      status,
    } = athleteLayer;

    const baseWorkout = blockLayer ?? athleteLayer;
    const athleteWorkout = blockLayer ? athleteLayer : undefined;

    const resolvedAssignedDate = assignedDate ?? '';

    return {
      context: {
        profileId,
        blockId,
        athleteVersion: version,
        blockVersion,
        blockLayerId: blockLayer?.id,
        athleteLayerId: athleteLayer.id,
        actualLayerId: actual?.id ?? '',
        workoutStatus: status,
      },
      assignedDate: resolvedAssignedDate,
      hasActual: !!actual,
      workout: mergeWorkoutLayersWithExternal(
        ACTUALS_SOURCE_RANKING,
        tagPrescriptionLayer(baseWorkout),
        tagPrescriptionLayer(athleteWorkout),
        tagPrescriptionLayer(modifier),
        actual,
      ),
    };
  });
};
